import {
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
} from 'd3-force'

export interface PhysicsConfig {
  homeStrength: number
  extHomeStrength: number
  charge: number
  chargeDistanceMax: number
  collidePadding: number
  collideIterations: number
  alphaDecay: number
  velocityDecay: number
  extLinkDistance: number
  extLinkStrength: number
  extRadius: number
  /** Rapporto larghezza/altezza dell'anello a fuoco (ellisse: il viewport è orizzontale). */
  ringAspect: number
  /** Spazio lungo il perimetro per ogni vicino (≈ larghezza di un box + margine). */
  ringSpacing: number
  /** Molla di casa dei nodi di sfondo a riposo (a fuoco usano `focusHomeStrength`). */
  bgHomeStrength: number
  /** A fuoco: molla di casa di fuoco e vicini, forte, così arrivano ESATTI sull'anello prima che alpha si spenga. */
  focusHomeStrength: number
  /** A fuoco: decadimento di alpha più lento, per lasciare il tempo di arrivare (transizione ~1 s). */
  focusAlphaDecay: number
  /** A fuoco: attrito alto, così la convergenza è senza rimbalzo. */
  focusVelocityDecay: number
  /** A fuoco: soglia alpha sotto cui la simulazione si ferma (più alta = si spegne prima). */
  focusAlphaMin: number
}

/** Costanti provvisorie (fisica B, spike 2026-09-23): taratura finale in Phase 2. */
export const DEFAULT_PHYSICS: PhysicsConfig = {
  homeStrength: 0.2,
  extHomeStrength: 0.2,
  charge: -90,
  chargeDistanceMax: 220,
  collidePadding: 4,
  collideIterations: 3,
  alphaDecay: 0.03,
  velocityDecay: 0.6,
  extLinkDistance: 250,
  extLinkStrength: 0.3,
  extRadius: 260,
  ringAspect: 1.6,
  ringSpacing: 200,
  bgHomeStrength: 0.04,
  focusHomeStrength: 0.8,
  focusAlphaDecay: 0.035,
  focusVelocityDecay: 0.7,
  focusAlphaMin: 0.2,
}

export interface RectCollideForce {
  (alpha: number): void
  initialize(nodes: SimNode[]): void
  iterations(): number
  iterations(n: number): RectCollideForce
}

/**
 * Collisione AABB sui box reali (n.w × n.h + padding): per ogni coppia
 * sovrapposta in x e y separa lungo l'asse di minor penetrazione, spostando
 * ciascun nodo di metà (per intero l'altro se uno è fissato da fx/fy).
 */
export function forceRectCollide(padding: number): RectCollideForce {
  let nodes: SimNode[] = []
  let iterations = 2
  const force = ((): void => {
    for (let it = 0; it < iterations; it++) {
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]!
          const b = nodes[j]!
          const dx = b.x - a.x
          const dy = b.y - a.y
          const ox = (a.w + b.w) / 2 + padding - Math.abs(dx)
          const oy = (a.h + b.h) / 2 + padding - Math.abs(dy)
          if (ox <= 0 || oy <= 0) continue
          const aFixed = a.fx != null || a.fy != null
          const bFixed = b.fx != null || b.fy != null
          const wa = aFixed ? 0 : bFixed ? 1 : 0.5
          const wb = bFixed ? 0 : aFixed ? 1 : 0.5
          if (ox < oy) {
            const sign = dx === 0 ? (i < j ? 1 : -1) : Math.sign(dx)
            a.x -= sign * ox * wa
            b.x += sign * ox * wb
          } else {
            const sign = dy === 0 ? (i < j ? 1 : -1) : Math.sign(dy)
            a.y -= sign * oy * wa
            b.y += sign * oy * wb
          }
        }
      }
    }
  }) as unknown as RectCollideForce
  force.initialize = (n) => {
    nodes = n
  }
  force.iterations = ((n?: number) => {
    if (n === undefined) return iterations
    iterations = n
    return force
  }) as RectCollideForce['iterations']
  return force
}

export interface SimNode {
  id: string
  x: number
  y: number
  /** Posizione di casa (dal layout a strati o dal cerchio degli esterni). */
  hx: number
  hy: number
  w: number
  h: number
  r: number
  ext?: boolean
  /** Residente non collegato al fuoco: resta in scena su un piano inferiore. */
  bg?: boolean
  /** Esterno in uscita dal fuoco: rientra nel nodo e svanisce prima di essere rimosso. */
  leaving?: boolean
  fx?: number | null
  fy?: number | null
}

export interface SimLink {
  source: string
  target: string
}

/** Simulazione d3-force pura (nessun DOM): molle di ritorno verso casa + repulsione + collisione + link esterni. */
export function createMapSimulation(
  nodes: SimNode[],
  extLinks: SimLink[],
  config: PhysicsConfig = DEFAULT_PHYSICS,
): Simulation<SimNode, undefined> {
  const sim = forceSimulation<SimNode>(nodes)
    .force(
      'home-x',
      forceX<SimNode>((d) => d.hx).strength((d) =>
        d.ext ? config.extHomeStrength : d.bg ? config.bgHomeStrength : config.homeStrength,
      ),
    )
    .force(
      'home-y',
      forceY<SimNode>((d) => d.hy).strength((d) =>
        d.ext ? config.extHomeStrength : d.bg ? config.bgHomeStrength : config.homeStrength,
      ),
    )
    .force(
      'charge',
      forceManyBody<SimNode>().strength(config.charge).distanceMax(config.chargeDistanceMax),
    )
    .force('collide', forceRectCollide(config.collidePadding).iterations(config.collideIterations))
    .force(
      'link',
      forceLink<SimNode, SimLink>(extLinks)
        .id((d) => d.id)
        // La distanza del link è quella tra le case dei due estremi (l'anello):
        // un valore fisso più corto dei raggi comprimerebbe l'anello verso il fuoco.
        .distance((l) => {
          // A runtime d3 ha già sostituito gli id con i nodi.
          const s = l.source as unknown as SimNode
          const t = l.target as unknown as SimNode
          const d = Math.hypot(t.hx - s.hx, t.hy - s.hy)
          return d > 0 ? d : config.extLinkDistance
        })
        .strength(config.extLinkStrength),
    )
    .alphaDecay(config.alphaDecay)
    .velocityDecay(config.velocityDecay)

  return sim
}

/**
 * Unico punto di ingresso/uscita dal fuoco su tutte le piattaforme: sostituisce
 * i nodi e i link esterni della simulazione, rivaluta le forze "casa", azzera
 * `fx`/`fy` e riavvia con un alpha alto.
 */
export function updateMapSimulation(
  sim: Simulation<SimNode, undefined>,
  nodes: SimNode[],
  extLinks: SimLink[],
  options: { focus?: boolean; config?: PhysicsConfig } = {},
): void {
  const config = options.config ?? DEFAULT_PHYSICS
  // A fuoco la fisica fa solo da animatore verso posizioni ESATTE (anello e
  // case): niente carica né collisione, altrimenti spostano i vicini
  // dall'anello e i raggi si incrociano. A riposo tornano entrambe.
  if (options.focus === true) {
    sim.force('charge', null)
    sim.force('collide', null)
    sim.alphaDecay(config.focusAlphaDecay)
    sim.velocityDecay(config.focusVelocityDecay)
    sim.alphaMin(config.focusAlphaMin)
  } else {
    sim.alphaDecay(config.alphaDecay)
    sim.velocityDecay(config.velocityDecay)
    sim.alphaMin(0.001)
    sim.force(
      'charge',
      forceManyBody<SimNode>().strength(config.charge).distanceMax(config.chargeDistanceMax),
    )
    sim.force(
      'collide',
      forceRectCollide(config.collidePadding).iterations(config.collideIterations),
    )
  }
  sim.nodes(nodes)
  const link = sim.force('link') as ReturnType<typeof forceLink<SimNode, SimLink>> | null
  link?.links(extLinks)
  const focusMode = options.focus === true
  const strength = (d: SimNode) =>
    // A fuoco la collisione è rimossa: anche i bg vanno a casa con la molla forte.
    focusMode
      ? config.focusHomeStrength
      : d.ext
        ? config.extHomeStrength
        : d.bg
          ? config.bgHomeStrength
          : config.homeStrength
  const homeX = sim.force('home-x') as ReturnType<typeof forceX<SimNode>> | null
  homeX?.x((d) => d.hx).strength(strength)
  const homeY = sim.force('home-y') as ReturnType<typeof forceY<SimNode>> | null
  homeY?.y((d) => d.hy).strength(strength)
  for (const n of nodes) {
    n.fx = null
    n.fy = null
  }
  sim.alpha(options.focus === true ? 1 : 0.6).restart()
}

/**
 * Anello di raggio minimo attorno a `center`, a partire da -π/2 in senso
 * orario, così `count` vicini non si sovrappongono prima che agisca la collisione.
 */
export function placeRing(
  center: { x: number; y: number },
  count: number,
  config: PhysicsConfig = DEFAULT_PHYSICS,
  startAngle = -Math.PI / 2,
): Array<{ x: number; y: number }> {
  if (count <= 0) return []
  // Ellisse rx = ry * ringAspect. Perimetro ≈ 2π·sqrt((rx² + ry²)/2): il raggio
  // verticale minimo è quello per cui ogni vicino ha `ringSpacing` di perimetro.
  const aspect = config.ringAspect
  const perimeterFactor = 2 * Math.PI * Math.sqrt((aspect * aspect + 1) / 2)
  const ry = Math.max(config.extRadius, (count * config.ringSpacing) / perimeterFactor)
  const rx = ry * aspect
  // Posti equidistanti per LUNGHEZZA D'ARCO (non per angolo: su un'ellisse i
  // punti a parametro uniforme si addensano ai lati). Campionamento numerico.
  const STEPS = 720
  const pts: Array<{ x: number; y: number; s: number }> = []
  let acc = 0
  let prev = { x: rx * Math.cos(startAngle), y: ry * Math.sin(startAngle) }
  pts.push({ ...prev, s: 0 })
  for (let i = 1; i <= STEPS; i++) {
    const a = startAngle + (i / STEPS) * 2 * Math.PI
    const cur = { x: rx * Math.cos(a), y: ry * Math.sin(a) }
    acc += Math.hypot(cur.x - prev.x, cur.y - prev.y)
    pts.push({ ...cur, s: acc })
    prev = cur
  }
  const total = acc
  const result: Array<{ x: number; y: number }> = []
  let j = 0
  for (let i = 0; i < count; i++) {
    const target = (i / count) * total
    while (j < pts.length - 1 && pts[j + 1]!.s < target) j++
    const p = pts[j]!
    result.push({ x: center.x + p.x, y: center.y + p.y })
  }
  return result
}

/** @deprecated usa `placeRing`. */
export function placeExternals(
  focus: { x: number; y: number },
  count: number,
  config: PhysicsConfig = DEFAULT_PHYSICS,
): Array<{ x: number; y: number }> {
  return placeRing(focus, count, config)
}
